import { canUseDOM } from '@ps/web-core-utils';
import { merge } from 'rxjs';
import { distinctUntilChanged, filter, map, skip } from 'rxjs/operators';

import type { EffectParams, WidgetEffectParams } from '@swf/core/store';
import { select } from '@swf/core/store';

/**
 * Returns an observable that runs on location change.
 * It does not run on first widget mount
 */
export const pageChange = (actions: EffectParams['actions']) =>
  canUseDOM
    ? actions.pipe(filter(({ type, payload }) => type === 'cms/update' && payload.url))
    : null;

export const urlParamChange = (params: WidgetEffectParams, urlParam: string) =>
  pageChange(params.actions)?.pipe(
    map(
      ({ state }) =>
        (select.cms.matchAlias(state, undefined)?.params as Record<string, unknown>)?.[urlParam]
    ),
    distinctUntilChanged()
  );

export const login = (actions: EffectParams['actions']) =>
  canUseDOM
    ? actions.pipe(
        filter(
          ({ type, state }) =>
            type === 'session/response' && select.session.isClientAuthenticated(state)
        )
      )
    : null;

export const logout = (actions: EffectParams['actions']) =>
  canUseDOM ? actions.pipe(filter(({ type }) => type === 'session/clear')) : null;

export const authChange = (actions: EffectParams['actions']) => {
  const loginObservable = login(actions);
  const logoutObservable = logout(actions);

  if (!loginObservable || !logoutObservable) {
    return null;
  }

  return merge(loginObservable, logoutObservable);
};

export const currencyChange = (actions: EffectParams['actions']) =>
  canUseDOM
    ? actions.pipe(
        distinctUntilChanged(
          (prev, curr) => select.currency.get(prev.state) === select.currency.get(curr.state)
        ),
        skip(1)
      )
    : null;

export const signUpLocationChange = (actions: EffectParams['actions']) =>
  canUseDOM
    ? actions.pipe(
        filter(
          ({ type, payload }) =>
            type === 'location/update' && payload.pathname && payload.pathname.includes('signup')
        )
      )
    : null;
